import React, { useState, useEffect, useRef } from "react";
import { getFlightDetials } from "../../api/apiService";
// Components
import Input from "../Input";
import Button from "../Button";
import Checkbox from "../Checkbox";
import { validateFlightField } from "../userForms/validators";
import { useSnackBar } from "../../components/snackBar/SnackBarContext"; // adjust the path as needed
// import Info from "../../assets/icons/ic_info.svg";

const AM_PM_TIME_REGEX = /^(0[1-9]|1[0-2]):([0-5][0-9])\s(AM|PM)$/;
const TIME_MASK_TEMPLATE = "--:-- --";
const TIME_EDITABLE_POSITIONS = [0, 1, 3, 4, 6];
const TIME_CONTROL_KEYS = new Set([
  "Tab",
  "Shift",
  "Control",
  "Alt",
  "Meta",
  "Escape",
  "Enter",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
]);

const sanitizeTimeInput = (value = "") =>
  value
    .toUpperCase()
    .replace(/[^0-9:\sAPM.-]/g, "")
    .replace(/\./g, ":");

// const getMeridiemFromValue = (value = "") =>
//   /\bP/i.test(value) || /PM/i.test(value) ? "PM" : "AM";

const getMeridiemFromValue = (value = "") => {
  if (/PM/i.test(value)) return "PM";
  if (/AM/i.test(value)) return "AM";
  return "--"; // 👈 DO NOT default to AM
};

const getMaskedTimeValue = (value = "") => {
  // Keep the visible value in a stable `HH:mm AM/PM` shell, even while partial.
  const sanitized = sanitizeTimeInput(value);
  const meridiem = getMeridiemFromValue(sanitized);
  const maskedValue = [...TIME_MASK_TEMPLATE];
  // maskedValue[6] = meridiem[0];
  // maskedValue[7] = "M";

  if (meridiem === "--") {
    maskedValue[6] = "-";
    maskedValue[7] = "-";
  } else {
    maskedValue[6] = meridiem[0];
    maskedValue[7] = "M";
  }

  if (!sanitized) {
    return maskedValue.join("");
  }

  if (AM_PM_TIME_REGEX.test(sanitized.trim())) {
    return sanitized.trim();
  }

  const [timePart = ""] = sanitized.split(/\s+/);
  const [rawHours = "", rawMinutes = ""] = timePart.split(":");

  rawHours
    .replace(/\D/g, "")
    .slice(0, 2)
    .split("")
    .forEach((digit, index) => {
      maskedValue[index] = digit;
    });

  rawMinutes
    .replace(/\D/g, "")
    .slice(0, 2)
    .split("")
    .forEach((digit, index) => {
      maskedValue[index === 0 ? 3 : 4] = digit;
    });

  return maskedValue.join("");
};

const normalizeAmPmTime = (value = "") => {
  const sanitized = sanitizeTimeInput(value).replace(/\s+/g, " ").trim();
  if (!sanitized) return "";

  const match = sanitized.match(/^(\d{1,2})[:\s]?(\d{2})\s*([AP])\s*M?$/);
  if (!match) return sanitized;

  const [, hourPart, minutePart, periodPart] = match;
  return `${hourPart.padStart(2, "0")}:${minutePart} ${periodPart}M`;
};

const isValidAmPmTime = (value = "") => AM_PM_TIME_REGEX.test(value);

const hasTimeDigits = (value = "") => /\d/.test(value);

const getNextEditablePosition = (position = 0) =>
  TIME_EDITABLE_POSITIONS.find((item) => item >= position) ??
  TIME_EDITABLE_POSITIONS[TIME_EDITABLE_POSITIONS.length - 1];

const getPreviousEditablePosition = (position = 0) =>
  [...TIME_EDITABLE_POSITIONS].reverse().find((item) => item < position) ??
  TIME_EDITABLE_POSITIONS[0];

const setInputCaret = (input, position) => {
  if (!input?.setSelectionRange) return;

  requestAnimationFrame(() => {
    input.setSelectionRange(position, position);
  });
};

const canApplyTimeDigit = (value, position, digit) => {
  const maskedValue = getMaskedTimeValue(value);

  if (position === 0) {
    return digit === "0" || digit === "1";
  }

  if (position === 1) {
    const firstDigit = maskedValue[0];
    if (firstDigit === "-") {
      return /[1-9]/.test(digit);
    }

    if (firstDigit === "0") {
      return /[1-9]/.test(digit);
    }

    if (firstDigit === "1") {
      return /[0-2]/.test(digit);
    }

    return false;
  }

  if (position === 3) {
    return /[0-5]/.test(digit);
  }

  if (position === 4) {
    return /\d/.test(digit);
  }

  return false;
};

const applyMaskedTimeInput = (value, cursorPosition, inputChar) => {
  if (!value) {
    const maskedValue = [...TIME_MASK_TEMPLATE];
    maskedValue[0] = inputChar;
    return {
      value: maskedValue.join(""),
      nextPosition: 1,
    };
  }

  // Apply one character at a time so the field behaves like a mask, not free text.
  const maskedValue = [...getMaskedTimeValue(value)];
  const normalizedChar = inputChar.toUpperCase();
  const targetPosition =
    normalizedChar === "A" || normalizedChar === "P"
      ? 6
      : getNextEditablePosition(cursorPosition);

  if (targetPosition === 6) {
    if (normalizedChar !== "A" && normalizedChar !== "P") {
      return null;
    }

    maskedValue[6] = normalizedChar;
    maskedValue[7] = "M";
    return {
      value: maskedValue.join(""),
      nextPosition: 6,
    };
  }

  if (!/\d/.test(normalizedChar)) {
    return null;
  }

  if (targetPosition === 1 && maskedValue[0] === "-") {
    if (!/[1-9]/.test(normalizedChar)) {
      return null;
    }

    maskedValue[0] = "0";
    maskedValue[1] = normalizedChar;
    return {
      value: maskedValue.join(""),
      nextPosition: 3,
    };
  }

  if (targetPosition === 4 && maskedValue[3] === "-") {
    maskedValue[3] = "0";
    maskedValue[4] = normalizedChar;
    return {
      value: maskedValue.join(""),
      nextPosition: 6,
    };
  }

  if (
    !canApplyTimeDigit(maskedValue.join(""), targetPosition, normalizedChar)
  ) {
    return null;
  }

  maskedValue[targetPosition] = normalizedChar;

  return {
    value: maskedValue.join(""),
    nextPosition: getNextEditablePosition(targetPosition + 1),
  };
};

const clearMaskedTimeInput = (value, cursorPosition, key) => {
  const maskedValue = [...getMaskedTimeValue(value)];
  let targetPosition =
    key === "Delete"
      ? getNextEditablePosition(cursorPosition)
      : getPreviousEditablePosition(cursorPosition);

  if (cursorPosition === 8 && key === "Delete") {
    targetPosition = 6;
  }

  if (!TIME_EDITABLE_POSITIONS.includes(targetPosition)) {
    return null;
  }

  if (targetPosition === 6) {
    maskedValue[6] = "-";
    maskedValue[7] = "-";
  } else {
    maskedValue[targetPosition] = "-";
  }

  const newValue = maskedValue.join("");

  // if fully cleared → reset properly
  if (!/\d/.test(newValue)) {
    return {
      value: "",
      nextPosition: 0,
    };
  }

  return {
    value: newValue,
    nextPosition: targetPosition,
  };
};

const FlightSet = ({
  isParticipant,
  index,
  flight,
  isOutBound,
  handleFlightChange,
  removeFlight,
  formErrors,
  setFormErrors,
  inputRefs,
}) => {
  const { showSnackbar } = useSnackBar();
  const [loading, setLoading] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const successSoundRef = useRef(null);
  const participantNumber = isParticipant ? 1 : 2;

  useEffect(() => {
    const audio = new Audio("/confirmation-tone.mp3");
    successSoundRef.current = audio;
  }, []);

  const handleChange = (e) => {
    handleFlightChange(flight, index, e.target.name, e.target.value);
  };

  const setFlightFieldError = (fieldName, error) => {
    setFormErrors((prev) => {
      const flightType = isOutBound ? "outboundFlights" : "inboundFlights";
      const flights = prev[flightType] ? [...prev[flightType]] : [];
      const flightErrors = flights[index] ? { ...flights[index] } : {};
      flightErrors[fieldName] = error;
      flights[index] = flightErrors;

      return { ...prev, [flightType]: flights };
    });
  };

  const updateTimeField = (fieldName, value, input, nextCaretPosition) => {
    handleChange({
      target: {
        name: fieldName,
        value,
      },
    });
    setFlightFieldError(fieldName, "");

    if (flight.flightBoundConfirmation)
      handleChange({
        target: {
          name: "flightBoundConfirmation",
          value: false,
        },
      });

    if (typeof nextCaretPosition === "number") {
      setInputCaret(input, nextCaretPosition);
    }
  };

  const handleTimeChange = (e) => {
    const rawValue = e.target.value;
    const normalizedValue = normalizeAmPmTime(rawValue);

    // Paste and browser autofill can bypass keydown, so re-shape the value here.
    updateTimeField(
      e.target.name,
      isValidAmPmTime(normalizedValue)
        ? normalizedValue
        : getMaskedTimeValue(rawValue),
      e.target,
    );
  };

  const handleTimeFocus = (e) => {
    const maskedValue = getMaskedTimeValue(flight[e.target.name]);
    handleChange({
      target: {
        name: e.target.name,
        value: hasTimeDigits(maskedValue) ? maskedValue : "",
      },
    });

    // setInputCaret(
    //   e.target,
    //   hasTimeDigits(maskedValue)
    //     ? getNextEditablePosition(maskedValue.indexOf("-"))
    //     : 0,
    // );
    if (!hasTimeDigits(maskedValue)) {
      setInputCaret(e.target, 0);
    } else {
      const firstDash = maskedValue.indexOf("-");
      setInputCaret(e.target, firstDash !== -1 ? firstDash : 0);
    }
  };

  const handleTimeKeyDown = (e) => {
    if (
      (e.ctrlKey || e.metaKey) &&
      ["a", "c", "v", "x"].includes(e.key.toLowerCase())
    ) {
      return;
    }

    if (TIME_CONTROL_KEYS.has(e.key)) {
      return;
    }

    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      const clearedValue = clearMaskedTimeInput(
        flight[e.target.name],
        e.target.selectionStart ?? 0,
        e.key,
      );

      if (clearedValue) {
        updateTimeField(
          e.target.name,
          hasTimeDigits(clearedValue.value) ? clearedValue.value : "",
          e.target,
          clearedValue.nextPosition,
        );
      }
      return;
    }

    if (e.key.length !== 1) {
      e.preventDefault();
      return;
    }

    const nextValue = applyMaskedTimeInput(
      flight[e.target.name],
      e.target.selectionStart ?? 0,
      e.key,
    );

    e.preventDefault();

    if (!nextValue) {
      return;
    }

    updateTimeField(
      e.target.name,
      nextValue.value,
      e.target,
      nextValue.nextPosition,
    );
  };

  const handleTimeBlur = (e) => {
    const currentValue = flight[e.target.name];
    const maskedValue = getMaskedTimeValue(currentValue);

    if (!hasTimeDigits(maskedValue)) {
      handleChange({
        target: {
          name: e.target.name,
          value: "",
        },
      });
      setFlightFieldError(e.target.name, "");
      return;
    }

    const normalizedValue = normalizeAmPmTime(maskedValue);
    handleChange({
      target: {
        name: e.target.name,
        value: normalizedValue,
      },
    });

    if (normalizedValue && !isValidAmPmTime(normalizedValue)) {
      setFlightFieldError(e.target.name, "Enter time in HH:mm AM/PM format");
      return;
    }

    setFlightFieldError(e.target.name, "");
  };

  const fetchAirFlightDetails = async () => {
    if (
      !setIsEdited ||
      !flight.departureDate ||
      flight.departureDate === "" ||
      !flight.flightNumber ||
      flight.flightNumber === ""
    )
      return;
    const departureDate = flight.departureDate?.trim().replace(/-/g, "");
    const flightNumber = flight.flightNumber;
    try {
      setLoading(true);
      showSnackbar("info", "Fetching flight details...");

      const response = await getFlightDetials(flightNumber, departureDate);
      const data = await response;
      if (data) {
        setLoading(false);
        showSnackbar("success", "Flight details auto-filled successfully.");
        setIsEdited(false);
      }

      const {
        flight_number,
        airline,
        leaving_from,
        going_to,
        departure_date,
        departure_time,
        arrival_date,
        arrival_time,
      } = data;

      if (flight_number.toLowerCase() !== flightNumber.toLowerCase()) {
        showSnackbar("error", `Failed to fetch flight details`);
        handleFlightChange(flight, index, "airline", "");
        handleFlightChange(flight, index, "arrivalTime", "");
        handleFlightChange(flight, index, "departureTime", "");
        handleFlightChange(flight, index, "arrivalDate", "");
        handleFlightChange(flight, index, "airportTo", "");
        handleFlightChange(flight, index, "airportFrom", "");
        if (flight.flightBoundConfirmation)
          handleChange({
            target: {
              name: "flightBoundConfirmation",
              value: false,
            },
          });
        return;
      }

      if (flight_number)
        handleFlightChange(flight, index, "flightNumber", flight_number);

      handleFlightChange(flight, index, "airline", airline || "");
      handleFlightChange(flight, index, "departureDate", departure_date || "");
      handleFlightChange(flight, index, "departureTime", departure_time || "");
      handleFlightChange(flight, index, "arrivalDate", arrival_date || "");
      handleFlightChange(flight, index, "arrivalTime", arrival_time || "");
      handleFlightChange(flight, index, "airportTo", going_to || "");
      handleFlightChange(flight, index, "airportFrom", leaving_from || "");
      if (flight.flightBoundConfirmation)
        handleChange({
          target: {
            name: "flightBoundConfirmation",
            value: false,
          },
        });
    } catch (err) {
      setLoading(false);
      setIsEdited(false);
      console.error("Failed to fetch flight details", err);
      showSnackbar("error", `Failed to fetch flight details`);
    }
  };

  return (
    <div className="relative border p-4 rounded mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="">Flight Segment {index + 1}</h3>
        {index > 0 && removeFlight && (
          <button
            onClick={() => removeFlight(index)}
            className="text-red-500 hover:underline text-sm"
          >
            Remove
          </button>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-70 flex flex-col items-center justify-center z-10">
            <div className="animate-spin rounded-full h-6 w-6 border border-blue-500 border-t-transparent"></div>
            <div className="pt-4 text-snackInfoBorder text-sm font-semibold">
              Auto Fetching Flight Details...
            </div>
          </div>
        )}
        <Input
          label="Flight Number"
          name="flightNumber"
          value={flight.flightNumber}
          placeholder="Ex : AI202"
          className="custom-placeholder"
          onBlur={(e) => {
            fetchAirFlightDetails();
          }}
          onChange={(e) => {
            handleChange(e);
            setIsEdited(true);
            if (flight.flightBoundConfirmation)
              handleChange({
                target: {
                  name: "flightBoundConfirmation",
                  value: false,
                },
              });
          }}
          ref={inputRefs.flightNumber}
          // error={formErrors.flightNumber}
        />
        <Input
          label="Departure Date"
          name="departureDate"
          type="date"
          value={flight.departureDate}
          min={new Date().toISOString().split("T")[0]}
          onBlur={(e) => {
            fetchAirFlightDetails();
          }}
          onChange={(e) => {
            // const today = new Date();
            // const selected = new Date(e.target.value);

            // // 🔹 If user typed or selected a past date, reset it
            // if (selected < today.setHours(0, 0, 0, 0)) {
            //   showSnackbar("error", "Past dates are not allowed.");
            //   handleChange({
            //     target: { name: "departureDate", value: "" },
            //   });
            //   return;
            // }

            handleChange(e);
            setIsEdited(true);
            if (flight.flightBoundConfirmation)
              handleChange({
                target: {
                  name: "flightBoundConfirmation",
                  value: false,
                },
              });
          }}
          ref={inputRefs.departureDate}
        />
        <Input
          label="Airline"
          name="airline"
          value={flight.airline}
          onChange={(e) => {
            handleChange(e);
            if (flight.flightBoundConfirmation)
              handleChange({
                target: {
                  name: "flightBoundConfirmation",
                  value: false,
                },
              });
          }}
          ref={inputRefs.airline}
          // error={formErrors.airline}
        />
        <Input
          label="Leaving From"
          name="airportFrom"
          value={flight.airportFrom}
          onChange={(e) => {
            handleChange(e);

            if (flight.flightBoundConfirmation)
              handleChange({
                target: {
                  name: "flightBoundConfirmation",
                  value: false,
                },
              });
          }}
          ref={inputRefs.airportFrom}
          // error={formErrors.airportFrom}
        />
        <Input
          label="Going To"
          name="airportTo"
          value={flight.airportTo}
          onChange={(e) => {
            handleChange(e);
            if (flight.flightBoundConfirmation)
              handleChange({
                target: {
                  name: "flightBoundConfirmation",
                  value: false,
                },
              });
          }}
          ref={inputRefs.airportTo}
          // error={formErrors.airportTo}
        />
        <Input
          label="Departure Time"
          name="departureTime"
          type="text"
          value={flight.departureTime || TIME_MASK_TEMPLATE}
          onChange={handleTimeChange}
          onFocus={handleTimeFocus}
          onKeyDown={handleTimeKeyDown}
          onBlur={handleTimeBlur}
          ref={inputRefs.departureTime}
          error={formErrors.departureTime}
        />
        <Input
          label="Arrival Date"
          name="arrivalDate"
          type="date"
          value={flight.arrivalDate}
          min={new Date().toISOString().split("T")[0]} // 🔹 disables past dates
          onChange={(e) => {
            // const today = new Date();
            // const selected = new Date(e.target.value);

            // // 🔹 Prevent typing an older date
            // if (selected < today.setHours(0, 0, 0, 0)) {
            //   showSnackbar("error", "Past dates are not allowed.");
            //   handleChange({
            //     target: { name: "arrivalDate", value: "" },
            //   });
            //   return;
            // }

            handleChange(e);
            if (flight.flightBoundConfirmation)
              handleChange({
                target: {
                  name: "flightBoundConfirmation",
                  value: false,
                },
              });
          }}
          ref={inputRefs.arrivalDate}
          // error={formErrors.arrivalDate}
        />
        <Input
          label="Arrival Time"
          placeholder="Arrival Time"
          name="arrivalTime"
          type="text"
          value={flight.arrivalTime || TIME_MASK_TEMPLATE}
          onChange={handleTimeChange}
          onFocus={handleTimeFocus}
          onKeyDown={handleTimeKeyDown}
          onBlur={handleTimeBlur}
          ref={inputRefs.arrivalTime}
          error={formErrors.arrivalTime}
        />
      </div>

      <Checkbox
        ref={inputRefs.flightBoundConfirmation}
        className="text-xs"
        name={`flightBoundConfirmation${
          index !== undefined
            ? isOutBound
              ? "1" + participantNumber + index
              : "2" + participantNumber + index
            : ""
        }`}
        checked={flight.flightBoundConfirmation}
        onChange={(e) => {
          const isChecked = e.target.checked;
          handleChange({
            target: {
              name: "flightBoundConfirmation",
              value: isChecked,
            },
          });

          if (isChecked && successSoundRef.current) {
            successSoundRef.current.currentTime = 0;
            successSoundRef.current.play();

            setTimeout(() => {
              successSoundRef.current.pause();
              successSoundRef.current.currentTime = 0;
            }, 2000);
          }
        }}
        label="I confirm that the flight information I entered is correct"
      />
      {/* {formErrors.flightBoundConfirmation && (
        <div className="flex gap-1 w-fit text-[10px] bg-red-100 text-red-600 mt-1 rounded p-[3px]">
          <img src={Info} alt="info" />
          {formErrors.flightBoundConfirmation}
        </div>
      )} */}
    </div>
  );
};

const AirTravelDetails = ({
  isParticipant = false,
  outboundFlights,
  setOutboundFlights,
  inboundFlights,
  setInboundFlights,
  formErrors,
  handleChange,
  setFormErrors,
  copyInBoundFlights,
  copyOutBoundFlights,
  allParticipantsData,
  inputRefs,
}) => {
  // const { showSnackbar } = useSnackBar();

  const handleOutboundChange = (flight, index, name, value) => {
    const fieldName = name.replace(/\d+$/, "");

    setOutboundFlights((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [fieldName]: value }; // ✅ new object
      return updated;
    });

    // Update errors as before
    const error = validateFlightField(fieldName, value, flight);
    setFormErrors((prev) => {
      const outboundFlights = prev.outboundFlights
        ? [...prev.outboundFlights]
        : [];
      const flightErrors = outboundFlights[index]
        ? { ...outboundFlights[index] }
        : {};
      flightErrors[fieldName] = error;
      outboundFlights[index] = flightErrors;
      return { ...prev, outboundFlights };
    });
  };

  const handleInboundChange = (flight, index, name, value) => {
    const fieldName = name.replace(/\d+$/, "");

    // ✅ Always clone the object at index
    setInboundFlights((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [fieldName]: value };
      return updated;
    });

    // ✅ Validation with cloned errors
    const error = validateFlightField(fieldName, value, flight);

    setFormErrors((prev) => {
      const inboundFlights = prev.inboundFlights
        ? [...prev.inboundFlights]
        : [];
      const flightErrors = inboundFlights[index]
        ? { ...inboundFlights[index] }
        : {};
      flightErrors[fieldName] = error;
      inboundFlights[index] = flightErrors;
      return { ...prev, inboundFlights };
    });
  };

  const addOutboundFlight = () => {
    setOutboundFlights([
      ...outboundFlights,
      {
        airportFrom: "",
        airportTo: "",
        airline: "",
        flightNumber: "",
        departureDate: "",
        departureTime: "",
        arrivalDate: "",
        arrivalTime: "",
      },
    ]);
  };

  const addInboundFlight = () => {
    setInboundFlights([
      ...inboundFlights,
      {
        airportFrom: "",
        airportTo: "",
        airline: "",
        flightNumber: "",
        departureDate: "",
        departureTime: "",
        arrivalDate: "",
        arrivalTime: "",
      },
    ]);
  };

  const removeOutboundFlight = (index) => {
    const updated = [...outboundFlights];
    updated.splice(index, 1);
    setOutboundFlights(updated);
  };

  const removeInboundFlight = (index) => {
    const updated = [...inboundFlights];
    updated.splice(index, 1);
    setInboundFlights(updated);
  };

  useEffect(() => {
    if (!outboundFlights || outboundFlights.length === 0) {
      addOutboundFlight();
    }

    if (!inboundFlights || inboundFlights.length === 0) {
      addInboundFlight();
    }
  }, [outboundFlights, inboundFlights]);

  // const copyUserFlightData = () => {
  //   const participantOutFlights = outboundFlights.map(
  //     ({ flightId, ...rest }) => ({
  //       ...rest,
  //     })
  //   );

  //   const participantInFlights = inboundFlights.map(
  //     ({ flightId, ...rest }) => ({
  //       ...rest,
  //     })
  //   );
  //   copyOutBoundFlights(participantOutFlights);
  //   copyInBoundFlights(participantInFlights);
  //   showSnackbar(
  //     "success",
  //     `${
  //       isParticipant
  //         ? "Flight details copy participant to user successfully."
  //         : "Flight details copy user to participant successfully."
  //     }`
  //   );
  // };

  return (
    <div>
      {/* Outbound Section */}
      <div className="mb-2">
        <h3 className="font-semibold">
          Outbound Flights (Leaving your home country )
        </h3>
        <p className="text-sm text-yellow-800">
          (Enter your flight number and departure date to automatically retrieve
          your flight details. Please review and confirm them. If any details
          aren’t filled in automatically or the auto generated information is
          incorrect, please enter them manually.)
        </p>
      </div>

      {/* {(isParticipant || allParticipantsData.length > 0) && (
        <Button
          className="flex justify-end border border-primary bg-transparent text-buttonColor"
          text={
            isParticipant
              ? "Copy Flight Details participant"
              : "Copy Flight Details user"
          }
          onClick={copyUserFlightData}
        />
      )} */}

      {outboundFlights &&
        outboundFlights.map((flight, index) => (
          <FlightSet
            isParticipant={isParticipant}
            key={index}
            index={index}
            flight={flight}
            setFormErrors={setFormErrors}
            handleFlightChange={handleOutboundChange}
            removeFlight={removeOutboundFlight}
            isOutBound={true}
            formErrors={formErrors?.outboundFlights?.[index] || {}}
            inputRefs={inputRefs}
          />
        ))}
      <div className="text-right mb-6 flex justify-end">
        <Button
          className="border border-primary bg-transparent text-buttonColor"
          onClick={addOutboundFlight}
          text="Connecting Flight"
        />
      </div>

      {/* Inbound Section */}
      {/* <h3 className="font-semibold mb-2">Inbound Flights</h3> */}
      <div className="mb-2">
        <h3 className="font-semibold">
          Inbound Flights (Returning to your home country )
        </h3>
        <p className="text-sm text-yellow-800">
          (Enter your flight number and departure date to automatically retrieve
          your flight details. Please review and confirm them. If any details
          aren’t filled in automatically or the auto generated information is
          incorrect, please enter them manually.)
        </p>
      </div>

      {inboundFlights &&
        inboundFlights.map((flight, index) => (
          <FlightSet
            isParticipant={isParticipant}
            key={index}
            index={index}
            isOutBound={false}
            setFormErrors={setFormErrors}
            flight={flight}
            handleFlightChange={handleInboundChange}
            removeFlight={removeInboundFlight}
            formErrors={formErrors?.inboundFlights?.[index] || {}}
            inputRefs={inputRefs}
          />
        ))}
      <div className="text-right mb-6 flex justify-end">
        <Button
          className="border border-primary bg-transparent text-buttonColor"
          onClick={addInboundFlight}
          text="Connecting Flight"
        />
      </div>
    </div>
  );
};

export default AirTravelDetails;
